import React, { useCallback, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { Link, useHistory } from 'react-router-dom'
import { Autocomplete, MenuItem, TextField } from '@mui/material'
import block from 'bem-cn'

import { ROUTE_URLS } from 'router/routes'

import { ReactComponent as SearchIcon } from 'shared/img/search.svg'

import { localeCommonKeysType } from 'shared/locale/en-US'
import { mediaSize } from 'shared/style/var'

import { excludeProvidersCasino, liveCasinoProviders } from 'modules/SlotsRoot/utils/slots'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'
import { selectGameList, selectGameListLoading } from 'features/slots/selectors'

import { useModalContext } from 'components/HOC/withModalQuery/ModalContext'

import { additionalSearchOptions, ISearchItem } from 'components/Header/data'
import { addNotify } from 'components/Notify'

import './Search.scss'

const b = block('search')

interface ISearchProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  placeholder?: string
}

const Search: React.FC<ISearchProps> = ({ isOpen, setIsOpen, placeholder }) => {
  const { push } = useHistory()
  const { pushNewModal } = useModalContext()
  const [searchQuery, setSearchQuery] = useState<ISearchItem>({
    id: '',
    route: '',
    name: '',
    imageUrl: '',
  })

  const isMobileOrTablet = useMediaQuery({ maxWidth: mediaSize.tablet })

  const locale = useSelector(selectLocaleDict, shallowEqual).common
  const localeSlots = useSelector(selectLocaleDict, shallowEqual).slots
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const gameList = useSelector(selectGameList, shallowEqual)
  const gameListLoading = useSelector(selectGameListLoading, shallowEqual)

  const handleChangeSelect = useCallback(
    (_, val: any) => {
      const newValue: ISearchItem = val as ISearchItem
      if (newValue) {
        if (!isAuth && newValue.authRequired) {
          addNotify(localeSlots.pleaseAuth)
        } else if (newValue.isModal) {
          pushNewModal(newValue.route)()
        } else push(newValue.route)
        setSearchQuery(prev => ({ ...prev, name: '' }))
      }
    },
    [isAuth, localeSlots, push, pushNewModal],
  )

  const handleChangeInput = useCallback((_, newValue: string) => {
    setSearchQuery(prev => ({ ...prev, name: newValue }))
  }, [])

  const noOptionsText = useMemo(() => {
    if (gameListLoading) return locale.loading
    if (searchQuery.name.length < 3)
      return locale.pleaseEnter.replace('NUMBER', (3 - searchQuery.name.length).toString())
    return locale.noOptions
  }, [gameListLoading, locale, searchQuery])

  const searchOptions: ISearchItem[] = useMemo(() => {
    const accumulator: ISearchItem[] = []

    gameList.forEach(slot => {
      accumulator.push({
        id: slot.gameId,
        name: slot.gameName,
        route: ROUTE_URLS.SLOTS_PROVIDER(slot.routeProvider, slot.gameId),
        authRequired: true,
        imageUrl: slot.imageUrl,
      })
      if (!excludeProvidersCasino.includes(slot.provider) && !accumulator.find(item => item.id === slot.provider)) {
        accumulator.push({
          id: slot.provider,
          name: slot.provider,
          route: ROUTE_URLS.SLOTS_PROVIDERS(slot.provider),
          imageUrl: slot.imageUrl,
        })
      }
    })

    liveCasinoProviders.forEach(provider => {
      accumulator.push({
        id: provider,
        name: provider,
        route: ROUTE_URLS.SLOTS_LIVE_PROVIDERS(provider),
      })
    })

    return [...additionalSearchOptions, ...accumulator]
  }, [gameList])

  const convertedOptions = useMemo((): ISearchItem[] => {
    if (searchQuery.name.length < 3 || gameListLoading) {
      return []
    }
    return searchOptions.map(option => ({
      ...option,
      name: locale[option.name as localeCommonKeysType]?.toString() || option.name,
    }))
  }, [gameListLoading, locale, searchOptions, searchQuery])

  return isOpen || isMobileOrTablet ? (
    <Autocomplete
      disableClearable={false}
      fullWidth
      className={b('autocomplete').toString()}
      value={searchQuery}
      noOptionsText={noOptionsText}
      options={convertedOptions}
      onChange={handleChangeSelect}
      onInputChange={handleChangeInput}
      getOptionLabel={(currentOption: ISearchItem | string) =>
        typeof currentOption === 'string' ? currentOption : currentOption.name
      }
      filterOptions={options =>
        options.filter(option => option.name.toLowerCase().trim().includes(searchQuery.name.toLowerCase().trim()))
      }
      isOptionEqualToValue={(opt: any, currVal: any) => currVal.value === '' || opt.value === currVal.value}
      renderOption={(renderOptionsProps, option: ISearchItem) => (
        <MenuItem className={b('item')} {...renderOptionsProps} key={option.id}>
          <Link to={option.route}>
            <img className={b('item-img')} src={option.imageUrl} alt="" />
            {option.name}
          </Link>
        </MenuItem>
      )}
      renderInput={params => (
        <TextField
          {...params}
          className={b('text-field', { isOpen }).toString()}
          InputProps={{
            ...params.InputProps,
            placeholder: placeholder || 'Search games',
            className: b('input').toString(),
            autoComplete: 'new-password',
            startAdornment: <SearchIcon className={b('icon')} />,
            endAdornment: params.InputProps.endAdornment,
          }}
        />
      )}
    />
  ) : (
    <div className={b('btn')} onClick={() => setIsOpen(true)}>
      <SearchIcon />
    </div>
  )
}

export default Search
